import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import type { ChatMessage, Citation, WidgetConfig } from "@shared/types";

import { apiFetch, createSession, getWidgetConfig, readSseStream } from "@web/lib/api";

type WidgetShellProps = {
  tenantSlug: string;
};

type UiMessage = ChatMessage & {
  pending?: boolean;
};

function getVisitorKey(tenantSlug: string) {
  return `cfcb:${tenantSlug}:visitor`;
}

function getStoredVisitorId(tenantSlug: string) {
  return localStorage.getItem(getVisitorKey(tenantSlug)) || undefined;
}

function setStoredVisitorId(tenantSlug: string, value: string) {
  localStorage.setItem(getVisitorKey(tenantSlug), value);
}

export function WidgetShell({ tenantSlug }: WidgetShellProps) {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [tenantName, setTenantName] = useState("");
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isSending]);

  useEffect(() => {
    let cancelled = false;
    const locale = navigator.language || "en";

    async function load() {
      try {
        const [configPayload, sessionPayload] = await Promise.all([
          getWidgetConfig(tenantSlug),
          createSession(tenantSlug, locale, getStoredVisitorId(tenantSlug))
        ]);

        if (cancelled) {
          return;
        }

        setConfig(configPayload.widgetConfig);
        setTenantName(configPayload.tenant.name);
        setSessionId(sessionPayload.session.id);
        setStoredVisitorId(tenantSlug, sessionPayload.session.visitorSessionId);
        setMessages(sessionPayload.messages);
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : "Failed to load chat");
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  const welcomeMessage = useMemo(
    () => config?.welcomeText || "Ask anything. I will answer from the approved knowledge base.",
    [config]
  );

  async function submitMessage(nextMessage: string) {
    if (!sessionId || !config || !nextMessage.trim() || isSending) {
      return;
    }

    setError(null);
    setIsSending(true);
    const locale = navigator.language || "en";
    const optimisticUser: UiMessage = {
      id: Date.now(),
      sessionId,
      role: "user",
      content: nextMessage,
      citations: [],
      provider: "local",
      model: "user-input",
      latencyMs: 0,
      createdAt: new Date().toISOString()
    };

    const pendingAssistant: UiMessage = {
      id: Date.now() + 1,
      sessionId,
      role: "assistant",
      content: "",
      citations: [],
      provider: "local",
      model: "streaming",
      latencyMs: 0,
      createdAt: new Date().toISOString(),
      pending: true
    };

    setMessages((current) => [...current, optimisticUser, pendingAssistant]);
    setInput("");

    try {
      const response = await fetch("/api/public/message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          tenantSlug,
          sessionId,
          message: nextMessage,
          locale
        })
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({ error: "Message request failed" }))) as {
          error?: string;
        };
        throw new Error(payload.error || "Message request failed");
      }

      let streamedText = "";
      let streamedCitations: Citation[] = [];
      await readSseStream(response, {
        onMeta(payload) {
          streamedCitations = payload.citations;
          setMessages((current) =>
            current.map((message) =>
              message.pending ? { ...message, citations: payload.citations } : message
            )
          );
        },
        onChunk(payload) {
          streamedText += payload.text;
          setMessages((current) =>
            current.map((message) =>
              message.pending ? { ...message, content: streamedText, citations: streamedCitations } : message
            )
          );
        },
        onDone(payload) {
          setMessages((current) =>
            current.map((message) =>
              message.pending
                ? {
                    ...payload.message,
                    content: streamedText.trim() || payload.message.content,
                    citations: streamedCitations
                  }
                : message
            )
          );
        }
      });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to send message");
      setMessages((current) => current.filter((message) => !message.pending));
    } finally {
      setIsSending(false);
    }
  }

  async function submitFeedback(messageId: number, value: "up" | "down") {
    if (!sessionId) {
      return;
    }
    await apiFetch("/api/public/feedback", {
      method: "POST",
      body: JSON.stringify({
        sessionId,
        messageId,
        value
      })
    }).catch(() => undefined);
  }

  if (error && !config) {
    return (
      <div className="widget-shell">
        <div className="widget-card widget-card-error">{error}</div>
      </div>
    );
  }

  return (
    <div
      className="widget-shell"
      style={
        {
          "--primary-color": config?.primaryColor || "#1747FF",
          "--accent-color": config?.accentColor || "#F4B942"
        } as CSSProperties
      }
    >
      <div className="widget-card">
        <div className="widget-header">
          <p className="widget-kicker">Text AI concierge</p>
          <h1>{config?.headerTitle || "Ask us anything"}</h1>
          <p>{tenantName || "Loading..."}</p>
        </div>

        <div className="widget-thread">
          {messages.length === 0 ? (
            <div className="welcome-panel">
              <p>{welcomeMessage}</p>
              <div className="starter-grid">
                {(config?.starterPrompts || []).map((prompt) => (
                  <button key={prompt} type="button" className="starter-chip" onClick={() => submitMessage(prompt)}>
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {messages.map((message) => (
            <article
              key={`${message.id}-${message.createdAt}`}
              className={`message ${message.role === "user" ? "message-user" : "message-assistant"}`}
            >
              <div className="message-body">
                <p>{message.content || (message.pending ? "Thinking…" : "")}</p>
              </div>
              {message.role === "assistant" && message.citations.length > 0 ? (
                <div className="citation-row">
                  {message.citations.map((citation) => (
                    <span key={`${message.id}-${citation.chunkId}`} className="citation-pill">
                      {citation.label}
                    </span>
                  ))}
                </div>
              ) : null}
              {message.role === "assistant" && !message.pending ? (
                <div className="feedback-row">
                  <button type="button" onClick={() => submitFeedback(message.id, "up")}>
                    Helpful
                  </button>
                  <button type="button" onClick={() => submitFeedback(message.id, "down")}>
                    Needs work
                  </button>
                </div>
              ) : null}
            </article>
          ))}
          <div ref={endRef} />
        </div>

        <form
          className="composer"
          onSubmit={(event) => {
            event.preventDefault();
            void submitMessage(input);
          }}
        >
          <textarea
            rows={2}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Type your question"
          />
          <button type="submit" disabled={!input.trim() || isSending}>
            {isSending ? "Sending…" : "Send"}
          </button>
        </form>

        {config?.disclaimer ? <p className="widget-disclaimer">{config.disclaimer}</p> : null}
        {error ? <p className="widget-error">{error}</p> : null}
      </div>
    </div>
  );
}
